import { useEffect, useRef, useState, RefObject } from 'react';

interface DragDropOptions {
    onDrop?: (event: DragEvent) => void;
    onDragStart?: (event: DragEvent) => void;
    onDragEnd?: (event: DragEvent) => void;
    onDragOver?: (event: DragEvent) => void;
}

interface DragDropResult<T extends HTMLElement> {
    ref: RefObject<T>;
    isDragging: boolean;
}

/**
 * Enables drag & drop only inside the element (and its children),
 * while disabling it elsewhere.
 */
export function useRestrictedDragDrop<T extends HTMLElement = HTMLElement>({
    onDrop,
    onDragStart,
    onDragEnd,
    onDragOver,
}: DragDropOptions = {}): DragDropResult<T> {
    const ref = useRef<T>(null);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        const targetEl = ref.current;
        if (!targetEl) return;

        const handleDragStart = (e: DragEvent) => {
            e.stopPropagation();
            e.dataTransfer?.setData('text/plain', '');
            e.dataTransfer!.effectAllowed = 'move';
            setIsDragging(true);
            onDragStart?.(e);
        };

        const handleDragOver = (e: DragEvent) => {
            if (targetEl.contains(e.target as Node)) {
                e.preventDefault();
                e.dataTransfer!.dropEffect = 'move';
                onDragOver?.(e);
            }
        };

        const handleDrop = (e: DragEvent) => {
            if (targetEl.contains(e.target as Node)) {
                e.preventDefault();
                setIsDragging(false);
                onDrop?.(e);
            }
        };

        const handleDragEnd = (e: DragEvent) => {
            e.stopPropagation();
            setIsDragging(false);
            onDragEnd?.(e);
        };

        // Prevent drag/drop outside of this element
        const disableOutside = (e: DragEvent) => {
            if (!targetEl.contains(e.target as Node)) {
                e.preventDefault();
                e.stopPropagation();
                if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
            }
        };

        targetEl.setAttribute('draggable', 'true');

        targetEl.addEventListener('dragstart', handleDragStart);
        targetEl.addEventListener('dragover', handleDragOver);
        targetEl.addEventListener('drop', handleDrop);
        targetEl.addEventListener('dragend', handleDragEnd);

        document.addEventListener('dragover', disableOutside, true);
        document.addEventListener('drop', disableOutside, true);

        return () => {
            targetEl.removeEventListener('dragstart', handleDragStart);
            targetEl.removeEventListener('dragover', handleDragOver);
            targetEl.removeEventListener('drop', handleDrop);
            targetEl.removeEventListener('dragend', handleDragEnd);
            document.removeEventListener('dragover', disableOutside, true);
            document.removeEventListener('drop', disableOutside, true);
        };
    }, [onDrop, onDragStart, onDragEnd, onDragOver]);

    return { ref, isDragging };
}
